import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { addApplicationItem } from '../../../graphql/ApplicationForm/applicationItem';
import { ActivityBox } from './ActivityBox';
import {
  type ActivityFormData,
  useGetActivity,
  useUpdateActivity,
} from '../../../graphql/ApplicationForm/extraActivity';
import { Loading, Alert, type AlertDetails } from '../../../../../components';
import { handleFileRename } from '../../../../../utils/handleFileRename';

type ActivityContentProps = {
  applicationNo: string;
  index: number;
  activityId?: string;
  enableEdit: boolean;
  enableDelete: boolean;
  handleDelete: () => void;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

export const ActivityContent: React.FC<ActivityContentProps> = (
  props: ActivityContentProps,
) => {
  const {
    applicationNo = '',
    activityId: initialActivityId = '',
    index: activityIndex,
    enableEdit,
    enableDelete,
    handleDelete,
    refetchApplication,
    handleLoading,
  } = props;
  const { applicationId } = useParams();

  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'error',
  };
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);
  const [activityId, setActivityId] = useState(initialActivityId);
  const [isCreateMode, setIsCreateMode] = useState<boolean>(!initialActivityId);

  const { activityFormData, loading, error } = useGetActivity(activityId);

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const createActivity = addApplicationItem('activities', refetchApplication);
  const updateActivity = useUpdateActivity();

  const handleSubmit = async (values: ActivityFormData): Promise<void> => {
    const { to, supportingDocuments, ...others } = values;
    const input = {
      to:
        to !== null && to !== undefined && to.toString().length > 0 ? to : null,
      supportingDocuments: supportingDocuments.map((doc, index) => {
        const { name, ...other } = doc;
        return {
          name: handleFileRename(applicationNo, 'activity', index + 1),
          ...other,
        };
      }),
      ...others,
    };

    await handleLoading(async () => {
      try {
        if (isCreateMode) {
          const res = await createActivity({
            variables: {
              id: applicationId,
              input: {
                activity: {
                  ...input,
                },
              },
            },
          });
          const resActivityId =
            res.data?.addApplicationItem.activities[props.index]._id;
          setActivityId(resActivityId);
          setIsCreateMode(!(resActivityId as string));
        } else {
          await updateActivity({
            variables: {
              id: activityId,
              ...input,
            },
          });
        }
        alertDetailDef = {
          alertContent: 'Saved Extra Activity Successfully.',
          alertType: 'success',
        };
      } catch (e) {
        alertDetailDef = {
          alertContent: 'Saved Extra Activity Failed.' + (e as Error).message,
          alertType: 'error',
        };
      } finally {
        setAlertDetails(alertDetailDef);
      }
    });
    setOpenAlert(true);
  };

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {loading ? (
        <Loading />
      ) : (
        <div>
          <ActivityBox
            initialActivityData={activityFormData}
            enableEdit={enableEdit}
            enableDelete={enableDelete}
            activityIndex={activityIndex}
            handleLoading={handleLoading}
            handleDelete={handleDelete}
            handleSubmit={handleSubmit}
          />
        </div>
      )}
    </>
  );
};
